import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import AvatarUploader from "@/components/AvatarUploader";
import { SignedLink } from "@/lib/signedFile";
import { toast } from "sonner";
import { Loader2, Save, User, X } from "lucide-react";

const EMPTY_SKILLS = [];

export default function StudentProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", city: "", avatar_url: "", bio: "",
    university: "", college: "", major: "", gpa: "",
    skills: EMPTY_SKILLS, cv_url: "", linkedin_url: "", github_url: "",
  });
  const [skillInput, setSkillInput] = useState("");
  const [cvUploading, setCvUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", phone: user.phone || "", city: user.city || "",
        avatar_url: user.avatar_url || "", bio: user.bio || "",
        university: user.university || "", college: user.college || "",
        major: user.major || "", gpa: user.gpa || "",
        skills: user.skills || [], cv_url: user.cv_url || "",
        linkedin_url: user.linkedin_url || "", github_url: user.github_url || "",
      });
      setLoading(false);
    }
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills.includes(s)) update("skills", [...form.skills, s]);
    setSkillInput("");
  };
  const removeSkill = (s) => update("skills", form.skills.filter((x) => x !== s));

  const uploadCV = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("الحجم أكبر من 25MB"); return; }
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("context", "cv");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("cv_url", data.file_id);
      toast.success("تم رفع السيرة الذاتية");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCvUploading(false); }
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      toast.success("تم حفظ الملف الشخصي");
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <DashboardLayout><div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div data-testid="student-profile">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">حسابي</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">الملف الشخصي</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">أكمل ملفك حتى تظهر في نتائج البحث للجمعيات والموجهين وتتمكن من التقديم على الفرص.</p>
        </div>

        <form onSubmit={save} noValidate className="space-y-6">
          <Card title="الصورة الشخصية">
            <AvatarUploader value={form.avatar_url} onChange={(u) => update("avatar_url", u)} shape="circle" fallbackIcon={User} testid="sp-avatar" />
          </Card>

          <Card title="البيانات الأساسية">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="الاسم الكامل" value={form.name} onChange={(v) => update("name", v)} testid="sp-name" required />
              <Field label="رقم الجوال" dir="ltr" value={form.phone} onChange={(v) => update("phone", v)} testid="sp-phone" />
              <Field label="المدينة" value={form.city} onChange={(v) => update("city", v)} testid="sp-city" />
            </div>
          </Card>

          <Card title="البيانات الأكاديمية">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="الجامعة" value={form.university} onChange={(v) => update("university", v)} testid="sp-univ" />
              <Field label="الكلية" value={form.college} onChange={(v) => update("college", v)} testid="sp-college" />
              <Field label="التخصص" value={form.major} onChange={(v) => update("major", v)} testid="sp-major" />
              <Field label="المعدل التراكمي (اختياري)" value={form.gpa} onChange={(v) => update("gpa", v)} testid="sp-gpa" placeholder="مثال: 4.5 / 5" />
            </div>
          </Card>

          <Card title="المهارات">
            <div className="flex gap-2 flex-wrap mb-3" data-testid="sp-skills-list">
              {form.skills.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-sm border border-teal-200">
                  {s}
                  <button type="button" onClick={() => removeSkill(s)} className="hover:text-red-600" aria-label={`إزالة ${s}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {form.skills.length === 0 && <span className="text-xs text-slate-500">أضف مهاراتك مثل: تحليل بيانات، Python، إدارة مشاريع...</span>}
            </div>
            <div className="flex gap-2">
              <input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="اكتب مهارة واضغط Enter" className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="sp-skill-input" />
              <button type="button" onClick={addSkill} className="px-4 py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="sp-skill-add">إضافة</button>
            </div>
          </Card>

          <Card title="المشرف الأكاديمي">
            {user?.is_graduate ? (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
                لا يوجد إشراف أكاديمي على حسابات الخرّيجين. استمتع بمرحلتك المهنية 🎓
              </div>
            ) : user?.supervisor_name ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div>
                  <div className="text-xs text-emerald-800">المشرف الحالي</div>
                  <div className="font-semibold text-slate-900" data-testid="sp-supervisor-name">{user.supervisor_name}</div>
                </div>
                <a href="/dashboard/supervision-requests" className="text-teal-700 text-sm font-semibold hover:underline">إدارة الإشراف →</a>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 rounded-lg bg-cream-50 border border-cream-200">
                <div className="text-sm text-slate-600">لم تُربَط بعد بمشرف أكاديمي. اختر مشرفك من قائمة جامعتك.</div>
                <a href="/dashboard/supervision-requests" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="sp-pick-supervisor">اختيار مشرفي</a>
              </div>
            )}
          </Card>

          <Card title="حالة الحساب الأكاديمية">
            <div className="p-3 rounded-lg bg-cream-50 border border-cream-200 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">
                  {user?.is_graduate ? (user?.gender === "أنثى" ? "أنتِ مسجّلة الآن كخرّيجة 🎓" : "أنت مسجّل الآن كخرّيج 🎓") : (user?.gender === "أنثى" ? "طالبة حالية في الجامعة" : "طالب حالي في الجامعة")}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {user?.is_graduate
                    ? "لن يتابع تقدّمك عضو هيئة تدريس، ولن تظهر في لوحاته."
                    : "يتابع تقدّمك عضو هيئة التدريس المشرف في جامعتك."}
                </div>
              </div>
              <button
                onClick={async () => {
                  const next = !user?.is_graduate;
                  if (!await appConfirm(next ? user?.gender === "أنثى" ? "هل تريدين التحويل إلى حساب خرّيجة؟" : "هل تريد التحويل إلى حساب خرّيج؟" : user?.gender === "أنثى" ? "هل تريدين العودة إلى حساب طالبة؟" : "هل تريد العودة إلى حساب طالب؟")) return;
                  try {
                    const { data } = await api.patch("/auth/profile", {
                      is_graduate: next,
                      graduation_date: next ? new Date().toISOString() : null,
                    });
                    if (data?.user && refresh) await refresh();
                    toast.success(next ? user?.gender === "أنثى" ? "تم التحويل لحساب خرّيجة" : "تم التحويل لحساب خرّيج" : user?.gender === "أنثى" ? "تم الرجوع لحساب طالبة" : "تم الرجوع لحساب طالب");
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600"
                data-testid="graduate-toggle"
              >
                {user?.is_graduate ? (user?.gender === "أنثى" ? "العودة لحساب طالبة" : "العودة لحساب طالب") : (user?.gender === "أنثى" ? "تخرّجتُ الآن" : "تخرّجت الآن")}
              </button>
            </div>
          </Card>

          <ChangePasswordCard />

          <Card title="السيرة الذاتية والروابط المهنية">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">السيرة الذاتية (PDF)</label>
                {form.cv_url ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <SignedLink fileId={form.cv_url} className="text-emerald-800 font-semibold hover:underline text-sm flex-1" testid="sp-cv-link">✓ تم رفع السيرة — عرض</SignedLink>
                    <button type="button" onClick={() => update("cv_url", "")} className="text-red-600 hover:text-red-700 text-sm" data-testid="sp-cv-remove">إزالة</button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 cursor-pointer">
                    {cvUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "رفع ملف PDF أو DOCX"}
                    <input type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => uploadCV(e.target.files?.[0])} data-testid="sp-cv-input" />
                  </label>
                )}
              </div>
              <Field label="LinkedIn" dir="ltr" value={form.linkedin_url} onChange={(v) => update("linkedin_url", v)} testid="sp-linkedin" placeholder="https://linkedin.com/in/..." />
              <Field label="GitHub / Portfolio" dir="ltr" value={form.github_url} onChange={(v) => update("github_url", v)} testid="sp-github" placeholder="https://github.com/..." />
            </div>
          </Card>

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="sp-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              حفظ التعديلات
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-cream-200 p-4 card-shadow">
      <h3 className="font-heading font-bold text-slate-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testid, dir, required, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input dir={dir} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid} required={required} placeholder={placeholder} />
    </div>
  );
}
